import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { Home, Library, LogOut, User } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { signOut } from '../lib/auth.ts';
import { cn } from '../lib/cn.ts';
import { useSession } from '../lib/session.tsx';
import { ThemeToggle } from './theme-toggle.tsx';

const studentLinks = [
  { to: '/student', icon: Home, labelKey: 'nav.home' },
  { to: '/student/vocab', icon: Library, labelKey: 'nav.vocab' },
  { to: '/student/profile', icon: User, labelKey: 'nav.profile' },
] as const;

const creatorLinks = [{ to: '/creator', icon: Home, labelKey: 'nav.home' }] as const;

export function SidebarNav() {
  const { t } = useTranslation();
  const { user, refresh } = useSession();
  const navigate = useNavigate();
  const { location } = useRouterState();
  const [armed, setArmed] = useState(false);
  const disarmTimer = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (disarmTimer.current !== null) window.clearTimeout(disarmTimer.current);
    };
  }, []);

  if (!user) return null;

  const links = user.role === 'creator' ? creatorLinks : studentLinks;

  const onSignOut = async () => {
    if (!armed) {
      setArmed(true);
      if (disarmTimer.current !== null) window.clearTimeout(disarmTimer.current);
      disarmTimer.current = window.setTimeout(() => {
        setArmed(false);
        disarmTimer.current = null;
      }, 3000);
      return;
    }
    if (disarmTimer.current !== null) {
      window.clearTimeout(disarmTimer.current);
      disarmTimer.current = null;
    }
    try {
      await signOut();
    } catch (err) {
      console.error('[sidebar] sign-out failed', err);
    }
    await refresh();
    void navigate({ to: '/' });
  };

  return (
    <aside className="hidden xl:flex fixed left-0 top-0 bottom-0 z-40 w-60 flex-col border-r border-border bg-bg-card/80 backdrop-blur-lg p-4">
      <Link to="/" className="font-heading text-xl font-bold text-fg mb-8 px-2">
        {t('nav.brand')}
      </Link>

      <nav className="flex flex-col gap-1 flex-1">
        {links.map(({ to, icon: Icon, labelKey }) => {
          // "My courses" (/student) must only highlight when the path has no
          // sibling route under /student/* — otherwise a sub-route like
          // /student/profile would light up both My courses and Profile.
          const active =
            location.pathname === to ||
            (to !== '/student' && location.pathname.startsWith(`${to}/`));
          return (
            <Link
              key={labelKey}
              to={to}
              className={cn(
                'flex items-center gap-3 px-3 py-2.5 rounded-lg text-[14px] font-body transition-colors duration-260 ease-[var(--ease-out-expo)] focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring',
                active
                  ? 'bg-accent-soft text-accent font-semibold'
                  : 'text-fg-2 hover:bg-bg-tint hover:text-fg',
              )}
            >
              <Icon className="size-[18px]" strokeWidth={active ? 2.5 : 1.5} />
              <span>{t(labelKey)}</span>
            </Link>
          );
        })}
      </nav>

      <div className="flex items-center gap-2 mt-auto pt-4 border-t border-border-s">
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-body text-fg truncate">{user.name || user.email}</p>
          {user.name && <p className="text-[11px] font-mono text-fg-3 truncate">{user.email}</p>}
        </div>
        <ThemeToggle />
        <button
          type="button"
          onClick={onSignOut}
          aria-label={armed ? t('common.confirmSignOut') : t('common.signOut')}
          title={armed ? t('common.confirmSignOut') : t('common.signOut')}
          className={cn(
            'inline-flex items-center justify-center size-9 rounded-lg border transition-colors duration-260 ease-[var(--ease-out-expo)] cursor-pointer focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring',
            armed
              ? 'border-error text-error bg-[var(--color-error-soft)]'
              : 'border-border bg-bg-card text-fg-2 hover:text-error hover:border-error',
          )}
        >
          <LogOut className="size-4" />
        </button>
      </div>
    </aside>
  );
}
